import querystring from 'querystring'
import axios from 'axios'
import axiosRetry from 'axios-retry'
import { io } from 'socket.io-client'
import { isCredentialBearingOperation } from './credentialOperations.coffee'
import { bodyParamsFromFirstArg, queryParamsFromRestArg } from './clientParamSplit.coffee'
import { serverErrorMessage } from './serverErrorMessage.coffee'
import { isStepUpConfirmation } from './stepUpOperations.coffee'
import { heldRoles, effectiveRoles, roleKeysFor, holdsRoleKeyFor, aclsForRoleKeys } from './roleKeys.coffee'
import { shouldRetryRequest } from './requestRetryPolicy.coffee'

axiosRetry axios,
  retries:10
  # What may be sent again, and why a POST is not: see requestRetryPolicy.coffee
  # (cards 6aa8b5d850645cd1c3e765b1, 6aa8b67e50645cd1c3e76605).
  retryCondition: (error) ->
    shouldRetryRequest error, {isNetworkError: axiosRetry.isNetworkError}
  retryDelay: (count, error) ->
    console.log "Retry Delay ERRROR:"
    console.dir error
    if count > 3
      autohide = if count > 7 then false else true
      if window.app?.emitter?
        window.app.emitter.emit 'global toast', "Please Check Your Internet Connection. Retrying... Attempt #{count}.", autohide
    axiosRetry.exponentialDelay(count)


# Socket-call inactivity timeout. Reset on every progress/activity event; on expiry
# we probe the server (by hash) before ever giving up. Kept short (2 min) so a
# genuinely-dead socket is detected fast — the long fan-outs survive it because the
# timer resets on activity and, even on a true idle gap, we re-attach (never re-fire).
SOCKET_IDLE_TIMEOUT_MS = 120000  # 2 minutes
# Consecutive batchStatus probe failures tolerated before the dup-guard stops
# assuming a dispatched job is still alive and settles the caller's promise with
# an error. Transient failures must not re-fire a live fan-out; a PERMANENT one
# must not hang the caller forever in silence.
MAX_PROBE_FAILURES = 3

# 128 crypto-strength bits, hex, for the socket listener room (card
# 6a715432c0243b672f4ffca7 — see the call site for why the room name is a
# capability). getRandomValues rather than randomUUID: it is available in
# insecure contexts too, where randomUUID is not, and this runs on every
# socket call including plain-http local pages.
#
# THE FALLBACK IS DELIBERATELY LOUD. Degrading silently to Math.random would
# leave a room that LOOKS like the fixed one and is not — the exact shape this
# card is about. Web Crypto is present in every browser this ships to, so in
# practice this branch is unreachable; if it ever fires, the console says so.
randomRoomSuffix = ->
  cryptoObj = globalThis?.crypto or self?.crypto
  if cryptoObj?.getRandomValues?
    bytes = cryptoObj.getRandomValues(new Uint8Array(16))
    return Array.from(bytes).map((b) -> b.toString(16).padStart(2, '0')).join('')
  console.warn "[client_api] Web Crypto unavailable — socket listener room falls back to
    non-cryptographic randomness. Progress/result rooms are guessable in this context."
  "#{Date.now()}#{Math.random().toString(36).substr(2, 5)}"


export default class ClientAPI
  constructor: (@baseURL, @api_source, @propsPrefix="$LoopBack$", options={}) ->
    console.log "CLIENT API Constructor: "
    @axios = axios
    @baseURL = @baseURL.replace /\/api$/, ''
    @headers = {}
    @socketExcludePatterns = options.socketExcludePatterns || []
    @blobOperations = options.blobOperations || []
    @[name] = @_load(name) for name in @props
    @resetAuthHeader()
    if /#/.test @currentStudentId
      delete @currentStudentId
    
    # Socket management for async jobs
    @socket = null
    @socketOwned = false  # Track if we created the socket or it was provided
    @activeCallbacks = new Map()  # Track active callbacks by listenerHash for cleanup

    # Cross-origin masquerade handoff (card 6a99dd74…7228): a portal opened as
    # <origin>/<path>#masq=<code> redeems the one-time code for its session.
    # Lives HERE so every shell of every application gets the behavior from
    # the constructor it already calls, under the storage prefix it already
    # owns — the two facts the opener cannot know across an origin boundary.
    @_adoptMasqueradeCode()

  # Redeem a #masq=<code> fragment: exchange it (single-use, short-TTL,
  # server-side — framework/masqueradeHandoff.coffee), install the session
  # under THIS shell's own propsPrefix, and reload clean. The fragment never
  # reaches any server (fragments are not sent in requests or Referer), and
  # it is stripped from the URL before the exchange resolves so what history
  # keeps is a code the store has already burned.
  _adoptMasqueradeCode: ->
    return unless typeof window != 'undefined' and window?.location? and typeof localStorage != 'undefined'
    m = /[#&?]masq=([A-Za-z0-9_-]{10,200})/.exec(window.location.hash or '')
    return unless m?
    # One adoption per page load, however many ClientAPI instances construct.
    return if ClientAPI._masqAdopted
    ClientAPI._masqAdopted = true
    code = m[1]
    cleaned = (window.location.hash or '').replace(/[#&?]masq=[A-Za-z0-9_-]+/, '')
    cleaned = '' if cleaned == '#'
    try window.history.replaceState(null, '', window.location.pathname + window.location.search + cleaned)
    axios.post("#{@baseURL}/api/masqueradeExchange", {code})
      .then (res) =>
        out = res?.data
        return unless out?.header?
        # Clear the prefix's whole session-key set before writing — these keys
        # are shared with whatever session last used this prefix in this
        # browser, and a stale currentStudentId survives a token swap (the
        # same reasoning as the same-origin masquerade open).
        try localStorage.removeItem(@propsPrefix + name) for name in @props
        localStorage.setItem @propsPrefix + 'accessTokenId', out.header
        localStorage.setItem @propsPrefix + 'currentUserId', "#{out.userId}"
        localStorage.setItem @propsPrefix + 'currentUserRole', "#{out.role ? ''}"
        window.location.reload()
      .catch (e) =>
        # An expired or already-used code just leaves the portal signed out —
        # its own sign-in screen is the accurate next step.
        console.log "masquerade handoff: exchange refused (#{e?.response?.status ? e?.message ? e})"

  # True for the page's own/self instance (page-origin, possibly via a
  # relative basePath). False for a re-pointed cross-origin instance such as
  # the federated Published downstream (D-025). Used to decide whether a 401
  # means "self session expired → log out + reload" (self only) vs "not
  # authorized on the downstream → just reject" (re-pointed instance).
  _isSelfInstance: ->
    return true if not @baseURL? or @baseURL == ''
    try
      # Host, not origin: behind a TLS-terminating proxy (ngrok, nginx) the
      # served config often carries an http:// basePath while the page runs
      # https:// — same host, same instance. Comparing origins made every
      # such deployment read as "re-pointed", which silently disabled the
      # whole self-instance 401 arm: the stale-token self-heal never ran and
      # boot fell through to an eternal spinner (card
      # 6a7bd623a51b17ac84335a00). A genuinely re-pointed downstream (D-025)
      # is a different host, which this still detects.
      return (new URL(@baseURL, window.location.href)).host == window.location.host
    catch
      return true

  # A 401 on a method call is ambiguous: the session may be dead (stale or
  # revoked token), or the session may be fine and this one method refused
  # (a per-method ACL denial). Ask the manifest endpoint with the current
  # headers — it runs the same authorizeToken path that minted the session,
  # answers 200 to a valid token AND to an anonymous request, and 401 only to
  # a presented-and-refused token. So only a 401 here proves the session is
  # dead. Anything else — 200, a network failure, a 5xx — is not proof, and
  # logging out on it would destroy a live session, so the session is kept.
  _sessionDead: ->
    return false unless @accessTokenId? and @accessTokenId != ''
    try
      await axios
        method: "get"
        url: @baseURL + @api_source
        headers: @headers
      return false
    catch probeError
      return probeError?.response?.status == 401

  loadSchema: () ->
    try
      res = await axios
        method: "get"
        url: @baseURL + @api_source
        headers: @headers
    catch error
      # Stale/invalid token: clear it and reload so the next /adminapi request
      # is anonymous and returns the manifest with the login form.
      if error?.response?.status == 401 and @_isSelfInstance()
        console.log "loadSchema: 401 — clearing stale token and reloading to login."
        @clearUser()
        # The admin route registry keys are '#login', '#dashboard', ... —
        # writing '/login' here minted '#/login' history entries that resolve
        # to no route, stranding the user on the '#loading' screen when they
        # back into one after re-login (card 6a7c9e840d81f47c9f9fb64b).
        window.location.hash = 'login'
        if window.location.search
          window.location.search = ''
        else
          window.location.reload()
        return new Promise (resolve, reject) -> # never resolves; page is reloading
      throw error
    @schema = res.data
    @basePath = @schema.basePath
    @setupSchema()
        
  setupSchema: () ->
    for path, functions of @schema.paths
      for method, signature of functions
        sig = signature.operationId.split(".")
        curObj = @
        for elem, index in sig
          if index < sig.length-1
            curObj[elem] = {} if elem not of curObj
            curObj = curObj[elem]
          else
            curObj[elem] = @createFunction path, method, signature
    
  httpCall: (path, operation, meta, param1, rest...) =>
    # HTTP call (original implementation)
    console.log "🌐 httpCall DEBUG - Operation: #{meta.operationId || "#{operation} #{path}"}"
    console.log "  📥 param1:", param1
    console.log "  📥 rest:", rest
    callbacks = null
    if rest.length > 0 && (rest[rest.length - 1]?.onProgress? || rest[rest.length - 1]?.onLog?)
      callbacks = rest.pop()
    basePath = @basePath
    headers = @headers
    o =
      method: operation
      url: @baseURL + basePath + path
      headers: headers

    # if we want to download a binary file, then we have to tell axios
    #   that's what we want. otherwise, it converts binary data to UTF-8
    #   automatically, which breaks everything
    if @blobOperations.includes(meta.operationId)
      o.responseType = 'blob'

    if param1?
      count = 0
      path_params = (swag_param for swag_param in meta.parameters when swag_param.in == "path")
      query_params = (swag_param for swag_param in meta.parameters when swag_param.in != "path")
      if path_params.length > 0
        for swag_param in path_params
          value = param1[swag_param.name]
          o.url = o.url.replace("{#{swag_param.name}}", value)

        # Declared NON-path arguments passed inside the FIRST object — the
        # single-object calling style, commit({id, log}) / diff({id, commitId}).
        # Only the PATH keys used to be read out of param1 here, so every body
        # argument was dropped and the server refused the call for a missing
        # required argument the caller had supplied. That is observation 1 on
        # card 6a6d34ecd3bc5a01f6ed7abf: the admin's Run-Function dialog got
        # HTTP 500 "log is required for commit." with the log filled in.
        #
        # The SOCKET transport has done exactly this since card
        # 6a695f2b5ff98dc8ecc23fbf, using this same helper; the HTTP transport
        # was simply never wired up. rest[0] still lands afterwards and WINS,
        # so the two-argument calling style is unchanged.
        firstArgParams = bodyParamsFromFirstArg query_params, param1

        if operation == "get"
          # NAMED ARGS, not a blanket ?filter=. The old code serialised the
          # second argument wholesale as `filter=<json>` regardless of what the
          # method declares, so `commitId` never reached req.query at all —
          # observation 2 on the same card. queryParamsFromRestArg applies the
          # test httpCall's own no-path-params branch already uses, so the two
          # branches now agree: an object naming a declared parameter travels
          # by name, anything else stays a filter spec.
          queryArgs = {}
          queryArgs[k] = v for own k, v of firstArgParams
          if rest?.length > 0
            for restArg in rest when restArg?
              named = queryParamsFromRestArg query_params, restArg
              queryArgs[k] = v for own k, v of named
          parts = []
          for own key, value of queryArgs when value != undefined
            encoded = if typeof value == 'object' then JSON.stringify(value) else value
            parts.push "#{key}=#{encodeURIComponent(encoded)}"
          o.url += "?" + parts.join("&") if parts.length
        else
          o.data = firstArgParams if Object.keys(firstArgParams).length > 0
          if rest?.length > 0
            for query_param, index in query_params
              if rest[index]?
                functionName = meta.operationId?.split('.')?.pop()
                if functionName == 'patchOne' && typeof rest[index] == 'object' && !('data' of rest[index])
                  o.data = {data: rest[index]}
                else if o.data? && typeof o.data == 'object' && typeof rest[index] == 'object' && !Array.isArray(rest[index])
                  # rest[index] WINS over anything the first object supplied.
                  merged = {}
                  merged[k] = v for own k, v of o.data
                  merged[k] = v for own k, v of rest[index]
                  o.data = merged
                else
                  o.data = rest[index]
      else if operation == "get"
        # Same two styles as the socket path: named-args find({filter: {...}})
        # is already keyed by parameter name, so send each named param as its
        # own query arg; wrapping the whole object under ?filter= buries the
        # real spec and the server matches every row silently.
        isNamedArgs = typeof param1 == 'object' and not Array.isArray(param1) and
          query_params.some((qp) -> qp.name of param1)
        if isNamedArgs
          parts = for qp in query_params when param1[qp.name] != undefined
            v = param1[qp.name]
            v = JSON.stringify(v) if typeof v == 'object'
            "#{qp.name}=#{encodeURIComponent(v)}"
          o.url += "?" + parts.join("&") if parts.length
        else
          o.url += "?filter=#{encodeURIComponent(JSON.stringify(param1))}"
      else
        o.data = param1
    #console.log "--> Generated URL #{o.url}"
    p = axios o
    #console.log "Path is #{path}"
    if /^[^.]+Auth$/.test(path) or meta.operationId?.match(/\.login/)
      return p.then (res) =>
        if res.status == 200
          @setUser(res.data.id, res.data.userId, res.data.role, '', res.data.roles, res.data.scopedRole)
          @resetAuthHeader()
          # Reload schema so subscriber-only methods (e.g. Account.getAccount)
          # become available before the caller proceeds. Without this, the
          # schema cached at boot was unauthenticated and missing those methods.
          return @loadSchema()
        res
      .catch (error) =>
        # The auth twin of the generic-path catch below (card
        # 6a6d3803d3bc5a01f6ed7ac4): rewrite axios's "Request failed with
        # status code NNN" to the server's own sentence ("Invalid
        # Credentials"), IN PLACE so `response`/`config`/`code` survive for the
        # step-up interceptor. What this branch must NOT copy from the generic
        # path is the self-instance 401 handling: a 401 here is a refused
        # login attempt, not an expired session, and clearing the user or
        # redirecting would loop the login page into itself. Interceptor-owned
        # refusals (REAUTH_REQUIRED step-up) are replayed at the axios layer
        # before this catch ever sees them.
        try
          message = serverErrorMessage error
          error.message = message if message? and message != error?.message
        catch
          # Unreadable body or frozen rejection — keep the original.
        throw error
    else
      return p.then (res) =>
        # Check if response contains a listener hash for async operations
        if res?.data?.listener?
          @_handleAsyncJob(res, callbacks)
        else
          res
      .catch (error) =>
        console.log "Received an error with network call:"
        console.dir error
        # Only the self/primary instance (page-origin, empty baseURL) treats a
        # 401 as a session expiry and logs the user out. A re-pointed instance
        # — e.g. the federated Published downstream (D-025) — has its own
        # baseURL; a 401 there means "not authorized on the downstream for this
        # method", not "your self session expired". Surface it as an error so
        # the caller (e.g. the Production tab on a terminal node) can handle it
        # without nuking the still-valid self session.
        #
        # THE STEP-UP CONFIRMATION IS EXEMPT (card 6a6e30adb6fa565d7504b9c6).
        # `<Model>Reauth` misses the auth branch's /^[^.]+Auth$/ selector, so a
        # wrong password in the step-up dialog arrived here and was read as a
        # session expiry — one typo destroyed the session it was elevating, and
        # ReauthDialog's own catch never ran because the page was already
        # reloading. A 401 answering a password you just typed is a REFUSED
        # PASSWORD, not an expired session; the rationale for keying on the
        # operation, and for excluding reauthStatus, is in stepUpOperations.
        #
        # AND EVEN A SELF-INSTANCE 401 IS NOT PROOF BY ITSELF (card
        # 6a7bd623a51b17ac84335a00): a per-method ACL denial answers 401 too,
        # and clearing the user on it logs a live session out over one refused
        # call. _sessionDead re-asks the manifest endpoint with the same
        # token; only when THAT also says 401 is the session actually dead and
        # the logout justified. Otherwise the error falls through to the
        # caller like any other refusal.
        isStepUp = isStepUpConfirmation meta?.operationId
        if error?.response?.status == 401 and @_isSelfInstance() and not isStepUp and await @_sessionDead()
          console.log "Unauthorized detected.  Clear User Attempt login."
          window.app?.emitter?.emit 'global toast', "Authorization Error.  Please Login Again."
          @clearUser()
          # 'login', not '/login' — see the loadSchema 401 handler above.
          window.location.hash = 'login'
          # in Safari, the window won't reload unless search changes
          if window.location.search
            window.location.search = ''
          else
            window.location.reload()

          ## stop processing here, since page is about to disappear ##
          return new Promise (resolve, reject) => resolve()
        else
          # Make the HTTP transport say what the SOCKET transport already says.
          # completeHandler above rejects with the server's own text
          # ("#{data.error.name}: #{data.error.message}"), while this path used
          # to re-throw axios's Error unchanged — whose `.message` is the
          # generic "Request failed with status code NNN". Every admin call site
          # (Show/List/Controller: `error.message || error.toString()`) then
          # rendered a status code where the server had written a reason. The
          # message is REPLACED IN PLACE rather than rethrown as a fresh Error
          # so that `response`, `config` and `code` survive: the step-up
          # interceptor (app-route.coffee) and Settings' danger-ack check read
          # `error.response.data.code`, and a hand-copied Error is one forgotten
          # property away from silently breaking them.
          #
          # THE WHOLE OPERATION IS GUARDED, extraction included. Reading the
          # message is the part that can throw: serverErrorMessage walks
          # properties off `error.response.data`, which is server-influenced
          # content, and a body carrying a throwing accessor —
          #   Object.defineProperty(data, 'message', {get: -> throw new Error()})
          # — makes the read itself blow up. With the call outside this try that
          # throw escapes the handler and REPLACES the axios rejection with a
          # bare Error, dropping the very `response`/`config`/`code` the in-place
          # rewrite above exists to preserve — silently, as a step-up dialog or a
          # danger-ack that simply never appears. Improving the message is a
          # nicety; keeping the rejection intact is not, so on any failure here
          # the original error is re-thrown untouched.
          try
            message = serverErrorMessage error
            error.message = message if message? and message != error?.message
          catch
            # Unreadable body or a frozen/exotic rejection value — keep the
            # original rather than fail.
          # ONE PLACE TO READ THE STATUS, WHATEVER THE TRANSPORT (card
          # 6aac3e67fb9b132bb83a8900). The socket arms already set `err.status`
          # (callViaSocket's responseHandler and _handleAsyncJob's deliver);
          # this arm left it only at `err.response.status`, so a caller that
          # has to tell "no such row" (404) from "the lookup failed" — an
          # expired session, an ACL refusal, a 500, a dropped connection — had
          # to know which transport its call happened to take and check both
          # places. One that checked only the axios shape read `undefined` over
          # the socket and reported every failure as "no such row", which is
          # how a support agent gets told a parent has no account and a second
          # one gets created.
          #
          # ADDITIVE, never a replacement: `error.response` stays exactly as it
          # was, because the step-up interceptor and the danger-ack check read
          # `error.response.data.code` and the whole reason this rejection is
          # rewritten in place is to keep those properties alive.
          try
            status = error?.response?.status
            error.status = status if status? and not error.status?
          catch
            # Same posture as the message rewrite above: a server-influenced
            # body with a throwing accessor must not replace the rejection.
          throw error
    


  # Transform parameters from new style to legacy style
  transformToLegacyParams: (operation, meta, params...) ->
    return params if params.length == 0
    console.log "Raw Params:"
    console.dir params

    # Special case for find, findOne, and count functions - they were designed to use natural format from the beginning
    if meta.operationId?.endsWith('.find') or meta.operationId?.endsWith('.findOne') or meta.operationId?.endsWith('.count') or meta.operationId?.endsWith('.findWithCount')
      console.log "  ✅ FIND/FINDONE/COUNT FUNCTION - treating as legacy format"
      return params

    # Get path and body parameters from meta
    pathParams = (param for param in meta.parameters when param.in == "path")
    bodyParams = (param for param in meta.parameters when param.in == "body")

    # STRICT-MODE CLASSIFIER, warn-only (card 6a6c2497d4a955ed6b43b3b2 —
    # stage 1 of the convention collapse). The legacy check below GUESSES:
    # any first-arg object carrying the FIRST declared parameter name passes
    # through untransformed, and a wrong guess is silent — per
    # clientParamSplit's contract an unagreed key "simply arrives undefined".
    # The dangerous shape is a first-arg object that MIXES declared and
    # undeclared keys (e.g. a new-style create(doc) whose document happens to
    # contain a field named like a declared parameter): the guess below reads
    # it as legacy and the row is never wrapped. Warn with enough detail to
    # convert the call site; behavior is deliberately unchanged — escalation
    # to throw is a later card, once fleet consoles are quiet.
    if params[0]? and typeof params[0] == 'object' and not Array.isArray(params[0]) and meta.parameters?.length > 0
      declaredNames = (param.name for param in meta.parameters)
      keys = Object.keys params[0]
      declaredKeys = (k for k in keys when k in declaredNames)
      if declaredKeys.length > 0 and declaredKeys.length < keys.length
        undeclared = (k for k in keys when k not in declaredNames)
        console.warn "client_api: AMBIGUOUS calling convention for #{meta.operationId ? operation}: first argument mixes declared keys [#{declaredKeys.join(', ')}] with undeclared keys [#{undeclared.join(', ')}] — the legacy/new guess may misread it. Declared parameters: [#{declaredNames.join(', ')}]. Spell the call as #{meta.operationId ? operation}({#{declaredNames.join(', ')}})."

    # If already in legacy format, return as-is
    # Legacy format: first param is object containing the expected parameter names from meta.parameters
    if params.length > 0 and typeof params[0] == 'object' and params[0]? and meta.parameters?.length > 0
      firstParam = params[0]
      firstMetaParam = meta.parameters[0]
      if firstMetaParam.name of firstParam
        console.log "  ✅ ALREADY LEGACY STYLE - no transformation needed"
        return params  # Already legacy format

    # Convert new style to legacy style
    console.log "  🔄 TRANSFORMING NEW STYLE → LEGACY STYLE"
    legacyParams = []

    if pathParams.length > 0
      # Build path params object from individual arguments (new style)
      pathObj = {}
      for pathParam, index in pathParams
        if index < params.length and params[index]?
          pathObj[pathParam.name] = params[index]
      legacyParams.push(pathObj)

      # Add body parameters from remaining arguments
      remainingParams = params[pathParams.length..]
      if remainingParams.length > 0 and bodyParams.length > 0
        # If single object passed, use it directly
        if remainingParams.length == 1 and typeof remainingParams[0] == 'object'
          legacyParams.push(remainingParams[0])
        else
          # Build body object from remaining arguments
          bodyObj = {}
          for bodyParam, index in bodyParams
            if index < remainingParams.length and remainingParams[index]?
              bodyObj[bodyParam.name] = remainingParams[index]
          legacyParams.push(bodyObj) if Object.keys(bodyObj).length > 0
    else
      # No path params - need to map parameters to their names from meta.parameters
      if meta.parameters?.length > 0 and params.length > 0
        # Create parameter dictionary using names from meta.parameters
        paramObj = {}
        for metaParam, index in meta.parameters
          if index < params.length and params[index]?
            paramObj[metaParam.name] = params[index]
        legacyParams.push(paramObj) if Object.keys(paramObj).length > 0
      else
        # Fallback for methods with no defined parameters
        legacyParams = params

    # Return transformed parameters
    return legacyParams

  createFunction: (path, operation, meta) ->
    op = (params...) =>
      console.log "📞 FUNCTION CALL:", meta.operationId || "#{operation} #{path}"
      console.log "  params:", params
      console.log "  meta:", meta

      # Check if last param is callbacks BEFORE transformation
      callbacks = null
      if params.length > 0
        lastParam = params[params.length - 1]
        if lastParam?.onProgress? || lastParam?.onLog?
          callbacks = params.pop()
          console.log "🎯 Callbacks detected before transformation:", callbacks

      # Transform parameters from new style to legacy style if needed
      legacyParams = @transformToLegacyParams(operation, meta, params...)

      # Add callbacks back to the end if they exist
      if callbacks?
        legacyParams.push(callbacks)
        console.log "📌 Re-added callbacks after transformation"

      # Try socket first if available and connected
      if @socket?.connected and @canUseSocketForAPI(meta)
        console.log "📡 CALLING VIA SOCKET with legacyParams:", legacyParams
        reauthRetried = false
        loop
          try
            result = await @callViaSocket(path, operation, meta, legacyParams[0], legacyParams.slice(1))
            if result? then return result
            break
          catch e
            # Step-up recovery for the SOCKET transport. The axios interceptor
            # only ever sees HTTP responses, and a QUEUED method sets
            # dispatched=true — which suppresses the HTTP fallback below. So a
            # flagged-and-queued method (Settings.saveEnvironmentVariables is
            # exactly that) used to surface a raw "Socket API error: Recent
            # authentication required…" with no dialog and no retry, the moment
            # an admin's elevation window lapsed mid-screen.
            if e?.code == 'REAUTH_REQUIRED' and @onReauthRequired? and !reauthRetried
              reauthRetried = true
              console.log "🔐 socket call needs step-up — prompting before retry"
              continue if await @onReauthRequired()
              throw e
            # If the job was already dispatched (server echoed our hash), a failure here
            # means we lost the connection AFTER it started — re-firing over HTTP would
            # duplicate the whole run (the bug that double-translated a collection). Do
            # NOT fall back; surface the error instead.
            if e?.dispatched
              console.error "[dup-guard] socket lost AFTER dispatch — REFUSING HTTP re-fire (would duplicate the run):", e
              throw e
            console.warn "[dup-guard] socket failed BEFORE dispatch — falling back to HTTP (safe, job never started):", e
            break

      # Fallback to original HTTP call
      console.log "🌐 CALLING VIA HTTP with legacyParams:", legacyParams
      # For HTTP, pass callbacks as last parameter just like socket
      @httpCall(path, operation, meta, legacyParams...)

    op.path = path
    op.method = operation
    #op.description = meta.summary
    op.parameters = meta.parameters
    #op.responses = meta.responses
    return op
 

  setLanguage: (lng) =>
    document.cookie = "languageOverride=#{lng}"

  resetAuthHeader: ->
    if @accessTokenId? and @accessTokenId != ''
      rgx = /^(.+?)Auth_(.+)$/
      resultArr = @accessTokenId.match rgx
      if !resultArr?
        # A stored token without the <Model>Auth_ framing cannot authenticate:
        # the server's parseAuthHeader ignores it and answers as ANONYMOUS.
        # This used to set the header FIRST and then guard the delete on the
        # header being absent — so the malformed value was sent anyway, and
        # the admin rendered a fake logged-in dashboard (stale role from
        # storage, empty model list) with no login form (card
        # 6a7cec86778ac869e5626a05). Treat it as no session at all: clear the
        # stored user so boot lands on the login form. clearUser re-enters
        # here once with a blank token, which takes the else branch and
        # removes any lingering header.
        console.error "Invalid Auth token — clearing stored session."
        @clearUser()
        @model = null
        return

      @headers.Authorization = @accessTokenId
      @model = resultArr[1]
    else if @headers?.Authorization?
      delete @headers.Authorization
      @model = null

  isLoggedIn: ->
    return @headers.Authorization? && @headers.Authorization != 'null'

  # Set socket connection from external source (like AppMain)
  setSocket: (socket) ->
    @socket = socket
    @socketOwned = false  # We don't own this socket

    # Listen for connection events to update our status
    if @socket?
      @socket.on 'connect', =>
        console.log "🚀 ClientAPI socket connected"
      @socket.on 'disconnect', =>
        console.log "📴 ClientAPI socket disconnected"

  # Establish socket connection with optional ownership for cleanup
  establishSocket: (owned = false) ->
    return @socket if @socket?  # Already established
    
    # transports: ['websocket'] — skip the HTTP long-polling handshake entirely.
    # When the server is behind a non-sticky load balancer (round-robin DNS,
    # multiple replicas) the polling handshake's followup requests can land on
    # a different replica and get rejected with "Session ID unknown" (HTTP 400),
    # putting the client in a reconnect loop. WebSocket upgrades are a single
    # request, so they pin to one replica for the lifetime of the connection.
    # Target this instance's baseURL when set (e.g. a downstream/Published
    # instance), so a re-pointed ClientAPI opens its socket against that origin
    # rather than the page origin. Empty baseURL → undefined → page origin
    # (unchanged default for the self instance).
    socketTarget = if @baseURL? and @baseURL != '' then @baseURL else undefined
    @socket = io(socketTarget, {auth: { token: @accessTokenId}, transports: ['websocket']})
    @socket.connect()
    @socketOwned = owned
    
    @socket

  # Clean up socket if we own it
  cleanupSocket: ->
    return unless @socket?

    if @socketOwned
      @socket.disconnect()

    @socket = null
    @socketOwned = false

  # Deregister a specific callback for a listener (used when component unmounts but job continues)
  deregisterCallback: (listenerHash) ->
    return unless listenerHash?
    if @activeCallbacks.has(listenerHash)
      console.log "🗑️ Deregistering callback for listener: #{listenerHash}"
      @activeCallbacks.delete(listenerHash)
    else
      console.log "⚠️ No callback found to deregister for: #{listenerHash}"

  # How long a job may go COMPLETELY SILENT before the client stops waiting.
  # Deliberately an idle timeout and not a deadline: an async job can legitimately
  # run for a long time (an AI function, a publish), and any progress, log or batch
  # event resets it — a job that is talking is a job that is alive. The window only
  # closes on silence, and even then the client asks the server for a retained
  # result before giving up, so this is the backstop behind the recovery rather
  # than the recovery itself.
  asyncJobIdleTimeoutMs: 10 * 60 * 1000

  # How long to wait for the server to answer "do you hold this job's result?".
  # Short: it is a redis GET on a socket that just acknowledged a join. Silence
  # past this means the server does not implement the handler at all, which is
  # a miss, not a delay.
  asyncJobAskTimeoutMs: 5000

  # Handle async jobs that return a listener hash.
  #
  # THE RACE THIS IS SHAPED AROUND (card 6aaadfeb71468172f64c4f3b). This used to
  # emit `join` first and register `transactionComplete_<hash>` last, several
  # round trips later. The server publishes a completion as a blind broadcast into
  # the hash's room with no replay, so a job that finished quickly — a controller
  # read that only consults local Redis state, say — published into an empty room
  # and the completion was gone. The caller's Promise then stayed pending for ever:
  # measured over a minute on el-feet, against a read the generated socket method
  # answered promptly, because ITS socket was already connected and joined.
  #
  # Three things close it, in this order:
  #   1. the completion listener is registered BEFORE the join, so the window is
  #      as small as the transport allows rather than as wide as the setup code;
  #   2. once joined, the client asks the server ONCE whether it already holds a
  #      result for this hash (framework/asyncJobResults.coffee retains every
  #      completion for a short window) and settles from it if so — a READ of an
  #      existing result, never a re-dispatch, so recovery cannot duplicate an
  #      executed job;
  #   3. a reconnect re-joins and re-asks, and an idle timeout asks one last time
  #      and then REJECTS with something the caller can act on, so no caller is
  #      left pending indefinitely.
  _handleAsyncJob: (response, callbacks = null) ->
    return response unless response?.data?.listener?

    listenerHash = response.data.listener
    socket = @establishSocket(true)  # Create owned socket for this job

    # Store callbacks in Map for potential deregistration
    if callbacks?
      @activeCallbacks.set(listenerHash, callbacks)

    return new Promise (resolve, reject) =>
      settled = false
      idleTimer = null
      progressHandler = null
      reconnectHandler = null

      cleanup = =>
        clearTimeout(idleTimer) if idleTimer?
        idleTimer = null
        if progressHandler
          socket.off "transactionProgress_#{listenerHash}", progressHandler
        socket.off 'console.log' if callbacks?.onLog?
        socket.off 'batchProgress'
        socket.off "transactionComplete_#{listenerHash}", completeHandler
        socket.off 'connect', reconnectHandler if reconnectHandler?
        try socket.emit 'leave', listenerHash
        @activeCallbacks.delete(listenerHash)  # Clean up callback from Map
        # Clean up socket if we own it
        @cleanupSocket() if @socketOwned

      # Settle exactly once, from whichever arm gets there first: the live
      # completion event, the retained result read back after the join, or the
      # timeout. Everything below funnels through here, which is what makes a
      # retained result arriving alongside a live event harmless.
      deliver = (data) =>
        return if settled
        settled = true
        cleanup()
        if data?.error?
          err = new Error("#{data.error.name}: #{data.error.message}")
          # Carry the machine-readable discriminator, exactly as callViaSocket's
          # responseHandler does — recovery flows key off `code`, not text, so a
          # 403 REAUTH_REQUIRED landing here as a bare Error made step-up
          # recovery inert down this path (card 6a7a0644618b82d537a408de).
          err.code = data.error.code if data.error.code?
          err.status = data.error.status if data.error.status?
          # The allowlisted structured payload (errorCodes.clientSafeErrorBody:
          # MFA enrollment challenge, DNS refusal list) — serialized into the
          # envelope since the codes landed, but dropped here until
          # group:dns-panel needed it. Only rides an allowlisted code.
          err.publicBody = data.error.publicBody if data.error.publicBody?
          # A completion event means the job ran server-side and failed — a
          # retry over HTTP would re-execute the whole run, the same duplicate
          # hazard callViaSocket's dup-guard exists to stop.
          err.dispatched = true
          reject(err)
        else
          resolve(data)

      giveUp = =>
        return if settled
        settled = true
        cleanup()
        err = new Error("The server stopped reporting on this job (#{listenerHash}) and no completion was retained for it. It may still be running; re-read the record rather than re-running the job.")
        err.code = 'ASYNC_JOB_RESULT_UNAVAILABLE'
        err.listener = listenerHash
        # DISPATCHED, like a server-side failure above: the job WAS handed to
        # the server, so an automatic retry would risk executing it twice. That
        # is the whole reason this rejects with a sentence about re-reading
        # rather than with something a recovery path would retry.
        err.dispatched = true
        reject(err)

      armIdleTimer = =>
        return if settled
        clearTimeout(idleTimer) if idleTimer?
        idleTimer = setTimeout =>
          # One last look before giving up — the completion may have been
          # published into a room this client had dropped out of.
          askForRetainedResult(giveUp)
        , @asyncJobIdleTimeoutMs

      # Ask the server whether it already holds this job's completion. Read-only
      # and idempotent; `found` is what distinguishes "the result is null" from
      # "there is no result", so a job that legitimately answers nothing still
      # settles.
      askForRetainedResult = (onMiss = null) =>
        return if settled
        answered = false
        miss = =>
          return if answered or settled
          answered = true
          onMiss?()
        try
          socket.emit 'job_result', listenerHash, (res) =>
            return if answered or settled
            answered = true
            if res?.found
              deliver(res.payload)
            else
              onMiss?()
        catch e
          console.log "job_result ask failed for #{listenerHash}:", e?.message
          return miss()
        # A server that predates this handler never calls the acknowledgement
        # back at all. Treat silence as a miss rather than hanging on it —
        # otherwise the timeout arm, whose whole job is to stop a caller
        # waiting for ever, would itself wait for ever.
        setTimeout miss, @asyncJobAskTimeoutMs

      # Listen for completion with hash-specific event name ONLY
      completeHandler = (data) =>
        console.log "Transaction complete in _handleAsyncJob:", data
        deliver(data)

      # ── REGISTERED BEFORE THE JOIN, on purpose. See the write-up above: the
      # old order left the whole of this setup inside the window in which a
      # fast job's completion could be published to nobody.
      socket.on "transactionComplete_#{listenerHash}", completeHandler

      # Listen for progress updates with hash-specific event name
      if callbacks?.onProgress?
        progressHandler = (data) =>
          console.log "📊 Progress update received in _handleAsyncJob:", data
          armIdleTimer()
          # Lookup callbacks from Map (allows deregistration)
          activeCallbacks = @activeCallbacks.get(listenerHash)
          if activeCallbacks?.onProgress?
            # Stamp the room this job attached to, exactly as callViaSocket's
            # own progressHandler does — the admin Stop button's batch_hash is
            # latched from this field, and the server's progress publishes
            # don't carry it, so without the stamp function_stop dies silently
            # on the re-attach path (card 6a73e1da13a90915fd0f0e79).
            activeCallbacks.onProgress(Object.assign({}, data, {listener: listenerHash}))
          else
            console.log "⚠️ Callback deregistered for #{listenerHash}"
        socket.on "transactionProgress_#{listenerHash}", progressHandler
        # NO generic listeners - each job has its own channel!

      if callbacks?.onLog?
        socket.on 'console.log', (data) =>
          armIdleTimer()
          if data?.payload?
            if Array.isArray(data.payload)
              callbacks.onLog(...data.payload)
            else
              callbacks.onLog(data.payload)
          else
            callbacks.onLog(data)

      # Handle batch progress events
      if callbacks?.onBatchProgress?
        socket.on 'batchProgress', (data) =>
          armIdleTimer()
          callbacks.onBatchProgress(data)
      else
        # Default batch progress handler - log to console
        socket.on 'batchProgress', (data) =>
          armIdleTimer()
          console.log "Batch progress: #{data.completed}/#{data.total} (job #{data.index + 1})"

      # A reconnect re-joins the room AND re-asks: socket.io does not replay
      # what was broadcast while the socket was away, and room membership does
      # not survive the round trip either. Without both halves a blip is
      # indistinguishable from the race this method exists to close.
      reconnectHandler = =>
        return if settled
        try socket.emit 'join', listenerHash, => askForRetainedResult()
        catch e
          console.log "re-join after reconnect failed for #{listenerHash}:", e?.message
      socket.on 'connect', reconnectHandler

      armIdleTimer()

      # THE JOIN, and the recovery read hanging off its acknowledgement. The
      # ack is the first moment this client is certain it would receive a
      # broadcast, so it is the right moment to ask whether one already
      # happened. `join` answers a callback (bootstrap's handler calls it on
      # both the allow and the refuse path); the bare-emit fallback below
      # covers a server old enough not to.
      joined = false
      onJoined = =>
        return if joined
        joined = true
        askForRetainedResult()
      try
        socket.emit 'join', listenerHash, -> onJoined()
      catch e
        console.log "join failed for #{listenerHash}:", e?.message
      # If no acknowledgement comes back, ask anyway rather than waiting on a
      # callback that is never going to arrive.
      setTimeout onJoined, 1000

  # WHAT THE SESSION REMEMBERS ACROSS A RELOAD. `currentUserRole` is the
  # PRIMARY/DISPLAY role and stays exactly what it always was — every existing
  # reader keeps working. `currentUserRoles` is the whole HELD SET, JSON-encoded
  # because _save writes strings, and `currentScopedRole` is the session's
  # stand-down scope. Together they let the client resolve an ACL the way the
  # server does (shared/roleKeys.coffee) instead of at one role — the defect
  # behind card 6a85ce2a76eff36e3533f6fc.
  #
  # localStorage is a CACHE of the server's answer, never the authority: roles
  # are added to a user and scopes are stamped on the TOKEN ROW, neither of
  # which this browser is told about. refreshUserRoles below re-reads both from
  # RoleScope, and every app that gates screens on a role should call it on
  # boot.
  props: ['accessTokenId', 'currentUserId', 'currentUserRole', 'currentUserRoles', 'currentScopedRole', 'currentStudentId']

  save_state: ->
    @_save(localStorage, name) for name in @props when name of @

  # `roles` and `scopedRole` are trailing and optional so every existing caller
  # (app-route's handoff install, clearUser) keeps its meaning. Passing neither
  # CLEARS them rather than leaving the last session's set behind — clearUser
  # is `@setUser()`, and a stale held set surviving a logout is exactly the
  # kind of thing that grants the next person the previous person's drawer.
  setUser: (@accessTokenId='', @currentUserId='', @currentUserRole, @currentStudentId='', roles=null, scopedRole=null) ->
    #console.log "Set User: #{@accessTokenId} - #{@currentUserId}"
    @currentUserRoles = if roles? then JSON.stringify(heldRoles({roles: roles})) else ''
    @currentScopedRole = scopedRole ? ''
    @save_state()

  # The held set as an array. Tolerant of every shape localStorage can hand
  # back — absent, '', a JSON array, or the bare primary role written by an
  # older build — because a parse failure here must degrade to the single-role
  # answer this class has always given, never to an exception on boot.
  storedRoles: ->
    raw = @currentUserRoles
    return [] unless raw? and raw != ''
    return raw if Array.isArray(raw)
    try
      parsed = JSON.parse(raw)
      return parsed if Array.isArray(parsed)
      return ["#{parsed}"] if parsed? and "#{parsed}".length
    catch
      return ["#{raw}"]
    []

  # The session as a "role holder" — the shape shared/roleKeys.coffee works on,
  # the same one a server principal presents. Built here so no caller has to
  # know which of the three stored fields means what.
  roleHolder: ->
    role: @currentUserRole
    roles: @storedRoles()
    scopedRole: (if @currentScopedRole? and @currentScopedRole != '' then @currentScopedRole else undefined)

  # THE ROLES THIS SESSION RESOLVES AT — the held set, or the scope alone when
  # the session is scoped. Same rule as the server's effectiveRoles, from the
  # same module.
  getUserRoles: ->
    effectiveRoles @roleHolder()

  # EVERY ACL key this session resolves at ("User.Agent", "User.Sales", …).
  # A session with no roles at all degrades to ["#{model}.undefined"], which
  # misses every exact entry and hits the "#{model}.*" wildcard — never to the
  # empty list, and never to "everything". See roleKeys.coffee.
  getRoleKeys: ->
    roleKeysFor @getUserModel(), @roleHolder()

  # Does this session hold `key`? The replacement for
  # `"#{model}.#{role}" == 'User.Admin'` at every client gate: a user holding
  # [Agent, Admin] passes an Admin gate, and an Admin scoped down to Agent does
  # not.
  holdsRoleKey: (key) ->
    holdsRoleKeyFor @getUserModel(), @roleHolder(), key

  # Is this session an Admin OF ITS OWN auth model? `getUserModel()` is the
  # model the token names, so an application signing into `Login` asks about
  # Login.Admin — the framework must not assume an auth model called 'User'.
  isAuthModelAdmin: ->
    @holdsRoleKey "#{@getUserModel()}.Admin"

  # Every ACL in `acls` that this session resolves at, exact keys and the
  # "#{model}.*" wildcard, unioned. [] means "no role-specific access", which
  # is what the drawer and the permission gates key their hide/deny on.
  aclsForSession: (acls) ->
    aclsForRoleKeys acls, @getRoleKeys()

  # Re-read the held set and the active scope from the SERVER and cache them.
  #
  # Why this exists at all: localStorage learns the roles a session had when it
  # was minted. Roles get added to a user, and a stand-down scope is stamped on
  # the token row by another tab — neither reaches this browser. RoleScope is
  # the server's own answer (it is what the stand-down control already reads),
  # it is granted to every signed-in principal by `access: 'read'`, and it costs
  # one GET on boot.
  #
  # NEVER THROWS AND NEVER CLEARS. An instance whose auth model does not expose
  # RoleScope, an offline boot, a 401 mid-flight — all leave the cached answer
  # exactly as it was, because degrading to "no roles" would hide the whole
  # drawer on a network blip. Returns true only when a fresh answer was stored.
  refreshUserRoles: ->
    model = @getUserModel()
    api = @[model]
    return false unless @isLoggedIn?() and api?.roleScope?
    try
      res = await api.roleScope()
      scope = res?.data ? res
      return false unless scope? and Array.isArray(scope.held)
      @currentUserRoles = JSON.stringify(scope.held)
      @currentScopedRole = scope.scoped ? ''
      # The primary/display role follows the same rule the server's `role`
      # method uses — the scope when scoped, else the first held role — so the
      # header and the audit trail keep agreeing with each other.
      @currentUserRole = scope.scoped ? (scope.held[0] ? @currentUserRole)
      @save_state()
      return true
    catch e
      console.log "refreshUserRoles: could not read role scope:", e?.message or e
      return false

  clearUser: ->
    @setUser()
    @resetAuthHeader()
    window.app?.onSignOut?()

  getUserRole: ->
    return @currentUserRole

  getUserModel: ->
    return @model

  isStudentSession: ->
    return @model == 'Student'

  logout: ->
    console.log "Client API LOGOUT Called!"
    if @isStudentSession()
      @Student?.logout?()
    else if !@currentStudentId && @model?
      @[@model].logout()

    window.app?.clearInitialRouting?()
    @clearUser()

  # ── SESSION STORAGE THAT SAYS WHEN IT IS NOT WORKING (card
  #    6aac3e67fb9b132bb83a8900).
  #
  # A session whose only home is localStorage, inside an iframe on a site that
  # is not the top-level one, is a session the browser may refuse to keep:
  # third-party storage is partitioned or blocked outright, and the refusal is
  # not subtle — reading `window.localStorage` at all THROWS a SecurityError
  # there. What this class did with that was the worst of the options: `_save`
  # swallowed the throw and said nothing, and `_load` did not catch at all, so
  # the constructor itself threw on boot.
  #
  # Either way the operator saw the same thing — a support panel that asks them
  # to sign in again, every time, with a perfectly good credential in hand and
  # nothing anywhere saying "this browser will not let me remember you". They
  # reasonably read that as a broken login.
  #
  # So: never throw, always know. `storageBlocked` is the fact a shell can
  # render, and the session still works IN MEMORY for the life of the page —
  # setUser assigns the fields on `this` before it ever reaches storage, so
  # what is lost is persistence across a reload, which is worth saying out loud
  # and is not the same as being signed out.
  #
  # sessionStorage is tried second because it survives a reload of the same tab
  # and is refused under different conditions than localStorage; where both are
  # refused there is nothing left to try.
  _stores: ->
    return @_storesCache if @_storesCache?
    @_storesCache = []
    for name in ['localStorage', 'sessionStorage']
      try
        store = window?[name]
        # Touch it. Merely resolving the property is not proof: a blocked
        # store can hand back an object whose every method throws.
        probe = "#{@propsPrefix}__probe"
        store.setItem probe, '1'
        store.removeItem probe
        @_storesCache.push store
      catch err
        # Not an error yet — the next store may work. Only having none is.
        null
    @_storesCache

  # True when nothing durable is available, so a shell can say so rather than
  # showing a sign-in screen for a session it is holding perfectly well.
  storageBlocked: -> @_stores().length == 0

  _save: (storage, name) ->
    key = @propsPrefix + name
    wanted = if name of @ && @[name]? && @[name] != '' then @[name] else null
    wrote = false
    for store in @_stores()
      try
        if wanted?
          store.setItem key, wanted
          # VERIFY. A quota-exceeded or partitioned store can accept setItem
          # and keep nothing; a write nobody checked is how "signed out again"
          # becomes invisible.
          wrote = true if store.getItem(key) == String(wanted)
        else
          store.removeItem key
          wrote = true
      catch err
        null
    if wanted? and not wrote
      console.error "[ClientAPI] this browser is not keeping #{key} — the session is in memory only and will not survive a reload"
    wrote

  _load: (name) ->
    key = @propsPrefix + name
    for store in @_stores()
      try
        val = store.getItem key
        return val if val?
      catch err
        null
    null

  # Public helpers for app code to read/write localStorage under this app's prefix
  saveLocal: (key, value) ->
    fullKey = @propsPrefix + key
    if value? and value isnt ''
      localStorage.setItem fullKey, value
    else
      localStorage.removeItem fullKey

  loadLocal: (key) ->
    localStorage.getItem @propsPrefix + key

  removeLocal: (key) ->
    localStorage.removeItem @propsPrefix + key

  # Check if this API call can be routed through socket
  canUseSocketForAPI: (meta) ->
    # Skip auth calls and file uploads through socket for now.
    # Credential-bearing operations must never take the socket path: the socket
    # dispatcher enqueues anything not on the direct-exec allowlist, and a
    # BullMQ job payload carries the raw args (plaintext password) plus the
    # caller's raw bearer token in access_control. A wrong password throws, the
    # job FAILS, and removeOnFail:10 keeps it resident in Redis — undoing the
    # "auth tokens hashed at rest" guarantee for those rows. The rule lives in
    # shared/credentialOperations so the server's socket dispatcher enforces
    # the SAME predicate — this client-side check is a routing nicety, not the
    # security boundary.
    return false if isCredentialBearingOperation(meta.operationId)
    return false if meta.operationId?.includes('upload')
    # Check caller-registered exclusions (e.g. binary-response operations)
    for pattern in @socketExcludePatterns
      return false if meta.operationId?.includes(pattern)

    # Now that we have proper response handling, allow most API operations through socket
    return true

  # Join @basePath to a swagger path key without doubling or dropping the
  # separator. The /adminapi manifest publishes basePath WITH a trailing slash
  # (bootstrap.coffee: `base_path+"/"`) while path keys carry NO leading slash
  # (model.coffee `short_path`), so hand-inserting a '/' between them yields
  # `/api//Deploys/batchStatus` — a doubled slash matches no express route and
  # 404s systematically. That 404 is what made the dup-guard's liveness probe
  # unanswerable; see _batchStatus and onTimeout below.
  _apiUrl: (suffix) =>
    base = String(@basePath or '').replace(/\/+$/, '')
    "#{@baseURL}#{base}/#{String(suffix).replace(/^\/+/, '')}"

  # Probe whether a fan-out is still running, keyed by the hash the client already
  # holds. Derives the batchStatus route from the function's own path (same model
  # prefix, method segment swapped). Returns {running, done, completed, total}.
  # Resolve which model's batchStatus route to probe.
  #
  # The route lives at the MODEL ROOT (Model/batchStatus), so only the first
  # path segment matters. Replacing just the LAST segment broke every
  # /:id/method route (Model/:id/commit → Model/:id/batchStatus, 404).
  #
  # But "this function's own model" is not always a valid choice: batchStatus
  # is registered by setupRemotes, which collection-less framework models never
  # run — Deploy and Settings expose no batchStatus route at all, while User
  # and Component do. Probing Deploys/batchStatus therefore 404s no matter how
  # the URL is joined, which is exactly the case the cloud panel hits.
  #
  # batchStatus is MODEL-INDEPENDENT — it reads Redis/BullMQ state by hash and
  # nothing else — so any registered route answers for any hash. Prefer this
  # model's own route when it has one (keeps the probe close to the caller),
  # then User, then whatever is registered. Same resolution jobStatusByHash
  # already uses.
  _batchStatusPath: (path) =>
    segments = path.split('/').filter (s) -> s.length > 0
    own = "#{segments[0]}/batchStatus"
    registered = (c.batchStatus.path for name, c of @ when c?.batchStatus?.path?)
    return own if own in registered
    return @User.batchStatus.path if @User?.batchStatus?.path?
    return registered[0] if registered.length
    own

  _batchStatus: (path, hash) =>
    url = @_apiUrl(@_batchStatusPath(path)) + '?hash=' + encodeURIComponent(hash)
    resp = await @axios.get(url, {headers: @headers})
    return resp?.data or {running: false, done: true}

  # Public status probe for a run hash (used by the function-run UI). batchStatus
  # is model-independent — it only reads Redis/BullMQ state by hash — so any
  # model's route works; prefer User, else the first registered model exposing
  # it. Returns {running, done, completed, total, job} where `job` is the live
  # BullMQ state (waiting/active/delayed/failed, attempts, failedReason) while
  # the job exists, null after completion.
  jobStatusByHash: (hash) =>
    op = @User?.batchStatus
    unless op?.path?
      for name, container of @ when container?.batchStatus?.path?
        op = container.batchStatus
        break
    return {running: false, done: true} unless op?.path?
    resp = await @axios.get(@_apiUrl(op.path) + "?hash=" + encodeURIComponent(hash), {headers: @headers})
    return resp?.data or {running: false, done: true}

  # Call API via socket using existing function.ModelName.functionName pattern
  callViaSocket: (path, operation, meta, param1, rest) ->
    return new Promise (resolve, reject) =>
      try
        console.log "🔧 callViaSocket inputs:"
        console.log "  param1:", param1
        console.log "  rest:", rest
        console.log "  rest length:", rest?.length

        # Check if the last parameter contains callbacks (onProgress, onLog)
        callbacks = null

        # Check in rest array - callbacks should be at the end after our createFunction handling
        if rest?.length > 0
          console.log "  Last element of rest:", rest[rest.length - 1]
          console.log "  Type of last element:", typeof rest[rest.length - 1]

          if rest[rest.length - 1]?.onProgress? || rest[rest.length - 1]?.onLog?
            callbacks = rest.pop()
            console.log "📲 Socket call with callbacks detected!"

        console.log "🔍 Final callbacks object:", callbacks

        # Extract model and function name from meta.operationId (e.g., "Component.findById")
        [modelName, functionName] = meta.operationId?.split('.') or ['Unknown', 'unknown']

        # Separate path parameters from post parameters for proper model function calling
        pathParams = {}
        postParams = {}

        if param1?
          path_params = (swag_param for swag_param in meta.parameters when swag_param.in == "path")
          query_params = (swag_param for swag_param in meta.parameters when swag_param.in != "path")

          if path_params.length > 0
            # Extract path parameters (like id for findById)
            for swag_param in path_params
              value = param1[swag_param.name]
              pathParams[swag_param.name] = value if value?

            # …and forward param1's NON-path arguments too — see
            # clientParamSplit for why. rest[0] still lands afterwards and
            # wins, so the two-argument calling style is unchanged.
            Object.assign postParams, bodyParamsFromFirstArg(query_params, param1)

            if rest?.length > 0 and rest[0]?
              if operation == "get"
                # NAMED ARGS, not a blanket filter — the same decision the HTTP
                # transport now makes, through the same helper, so the two
                # transports agree about what a caller's second argument means.
                #
                # This used to be `postParams.filter = rest[0]` ("like
                # httpCall", which is exactly the problem — it copied the
                # defect). The socket dispatcher rebuilds the positional vector
                # from postParams BY NAME, so diff({id}, {commitId}) arrived
                # with `filter` set and `commitId` absent, the server pushed
                # null into that slot, and the method was refused for a missing
                # required argument the caller had supplied.
                #
                # ABSORBED DELIBERATELY onto card 6a6d34ecd3bc5a01f6ed7abf,
                # which names httpCall. The card exists so that a declared body
                # argument ARRIVES, and the admin PREFERS the socket: fixing
                # only httpCall would have shipped a card whose stated benefit
                # never materialised on the transport the application actually
                # uses. Checked against the feature, not against the defect.
                Object.assign postParams, queryParamsFromRestArg(query_params, rest[0])
              else
                # Special case for patchOne: wrap data if not already wrapped
                # Supports both calling mechanisms:
                # - New: patchOne({id: x}, {status: "failed"})
                # - Legacy: patchOne({id: x, data: {status: "failed"}})
                if functionName == "patchOne" && rest[0]? && typeof rest[0] == 'object' && !('data' of rest[0])
                  postParams.data = rest[0]
                else
                  Object.assign(postParams, rest[0])
          else
            # No path params - param1 is the filter/data, rest[0] contains includes etc
            if operation == "get"
              # Two calling styles are legal here: the natural spec
              # find({where: ...}) and the named-args style find({filter: {...}}).
              # The server maps postParams to arguments BY NAME (bootstrap socket
              # handler), so named-args must pass through as-is — wrapping them
              # again buries the real spec under filter.filter and the server-side
              # Filter, seeing no top-level where, silently matches EVERY row.
              isNamedArgs = typeof param1 == 'object' and not Array.isArray(param1) and
                query_params.some((qp) -> qp.name of param1)
              if isNamedArgs
                Object.assign(postParams, param1)
              else
                postParams.filter = param1
            else
              Object.assign(postParams, param1)
            if rest?.length > 0 and rest[0]?
              Object.assign(postParams, rest[0])
        else if rest?.length > 0 and rest[0]?
          Object.assign(postParams, rest[0])

        # Generate a unique listener hash for this request (similar to Schema.coffee pattern).
        #
        # THIS NAME IS A CAPABILITY, not a correlation id (card 6a715432c0243b672f4ffca7).
        # It is the pub/sub room the server publishes this call's progress and results
        # into, and bootstrap.coffee's canJoinRoom deliberately does NOT gate rooms of
        # this shape — its comment states the AI function rooms are protected "by making
        # the run hash unguessable per run". On the socket transport that hash is THIS
        # one: an exposed AIFunction is not on the direct-exec allowlist, so it is queued
        # and runs on the worker with this string as its room, start to finish. So the
        # unguessability the join policy relies on is a property of this line.
        #
        # It used to be Date.now() + Math.random().toString(36).substr(2, 5) — a
        # predictable millisecond stamp plus ~6e7 of NON-cryptographic randomness, after
        # a public model and function name. Not practically brute-forcible over a socket,
        # which is why the card stayed LOW, but not what the policy above claims either.
        # 128 crypto-strength bits makes the claim true.
        listenerHash = "socketAPI#{modelName}#{functionName}#{randomRoomSuffix()}"

        # Join the room for this specific response (non-blocking)
        @socket.emit 'join', listenerHash

        # Store callbacks in Map for potential deregistration
        if callbacks?
          @activeCallbacks.set(listenerHash, callbacks)

        # Forward declare handlers
        responseHandler = null
        progressHandler = null
        queuedHandler = null
        anyHandler = null
        channelEvent = "transactionComplete_#{listenerHash}"

        # Once the server echoes our hash back, the run is OWNED server-side. After
        # that, a dropped socket / idle period means we merely stopped HEARING about
        # it — never that it failed — so we must re-attach, never re-invoke. This flag
        # gates that (see the dispatcher's catch and onTimeout below).
        dispatched = false
        probeFailures = 0
        reconnectHandler = null
        # Set by cleanupSocket, which every settling path already goes through.
        # The recovery arms below key off it so a late answer cannot re-settle a
        # promise or re-join a room this call has finished with.
        settled = false

        # Shared teardown for this listener's socket subscriptions. Must remove
        # EVERY per-call listener — a leftover transactionQueued handler leaked
        # one closure per RPC and let a late enqueue-ack re-arm the idle timer
        # on an already-settled promise (zombie batchStatus polling).
        cleanupSocket = =>
          settled = true
          @socket?.off channelEvent, responseHandler
          @socket?.off "transactionProgress_#{listenerHash}", progressHandler
          @socket?.off "transactionQueued_#{listenerHash}", queuedHandler if queuedHandler?
          @socket?.off 'connect', reconnectHandler if reconnectHandler?
          @socket?.offAny anyHandler if anyHandler?
          if callbacks?.onLog?
            @socket?.off 'console.log'
          @socket?.emit 'leave', listenerHash
          @activeCallbacks.delete(listenerHash)

        # ASK THE SERVER WHETHER IT ALREADY HOLDS THIS JOB'S COMPLETION (card
        # 6aab65a36c29c3a6dc3bbbb3). Every completion in the framework is retained
        # under its hash for a window (framework/asyncJobResults.coffee) and read
        # back through this handler; `_handleAsyncJob` has recovered that way
        # since card 6aaadfeb71468172f64c4f3b, but THIS transport — the one every
        # generated model method and every AI function rides — never asked.
        #
        # It is a READ of a result that already exists, never a re-dispatch, so
        # it cannot execute a job twice. `found` is what distinguishes "the answer
        # is nothing" from "there is no answer"; a miss hands control to onMiss,
        # which is the behaviour this call had before the read existed.
        askForRetainedResult = (onMiss = null) =>
          return if settled
          answered = false
          miss = =>
            return if answered or settled
            answered = true
            onMiss?()
          try
            @socket?.emit 'job_result', listenerHash, (res) =>
              return if answered or settled
              answered = true
              if res?.found
                console.log "[dup-guard] recovered the retained completion for #{listenerHash} — the socket missed it, the server did not"
                responseHandler(res.payload)
              else
                onMiss?()
          catch e
            console.log "job_result ask failed for #{listenerHash}:", e?.message
            return miss()
          # A server too old to implement the handler never calls the
          # acknowledgement back at all. Treat silence as a miss rather than
          # hanging on it.
          setTimeout miss, (@asyncJobAskTimeoutMs ? 5000)

        # A RECONNECT RE-JOINS AND RE-ASKS. socket.io replays nothing that was
        # broadcast while the socket was away, and room membership does not
        # survive the round trip either — so without both halves a blip costs the
        # caller the result, which is exactly what the AI Worksheet button did.
        reconnectHandler = =>
          return if settled
          console.log "[dup-guard] socket reconnected (hash=#{listenerHash}) — re-joining and asking for anything we missed"
          try @socket?.emit 'join', listenerHash
          catch e then console.log "re-join after reconnect failed for #{listenerHash}:", e?.message
          askForRetainedResult()

        # Inactivity timer. If the job was never dispatched, reject so the caller can
        # legitimately retry over HTTP. If it WAS dispatched, never re-fire: ask the
        # server (by the hash we already hold) whether it's still running — re-join and
        # keep waiting if so; resolve if it finished while our socket was away (we'd
        # have missed the completion event). This is the fix for duplicate fan-outs.
        onTimeout = =>
          unless dispatched
            console.warn "[dup-guard] idle timeout BEFORE dispatch (hash=#{listenerHash}) — rejecting; caller may retry over HTTP (safe: job never started)"
            cleanupSocket()
            err = new Error("Socket API timeout for #{eventName}")
            err.dispatched = false
            return reject(err)
          # THE RESULT FIRST, THE STATE SECOND. batchStatus can only say WHETHER
          # the job finished — it never carries what it produced, and a job whose
          # record has been reaped answers {done: true, job: null}. Deciding from
          # that alone is how a finished AI run was handed back as an envelope
          # with no data in it. Ask for the retained completion; only when there
          # genuinely is none does the probe below get to decide anything.
          askForRetainedResult(probeBatchStatus)

        probeBatchStatus = =>
          return if settled
          console.log "[dup-guard] idle #{SOCKET_IDLE_TIMEOUT_MS/1000}s on dispatched job (hash=#{listenerHash}) — probing batchStatus before doing ANYTHING (never re-fire)"
          status = {running: true}
          try
            status = await @_batchStatus(path, listenerHash)
            probeFailures = 0
            console.log "[dup-guard] batchStatus(hash=#{listenerHash}) ->", status
          catch e
            probeFailures += 1
            # Assuming "still running" is the right call for a TRANSIENT probe
            # failure — re-firing a live fan-out is worse than waiting. But it
            # must not be assumed forever: when the probe is systematically
            # unanswerable (a 404 route, a revoked token) this branch re-arms
            # the timer on every tick, so the caller's promise never settles and
            # the operator sees a click that did nothing, with no error at all.
            # Bound it: after MAX_PROBE_FAILURES give up and SAY SO, rather than
            # hanging silently. Still never re-fires — the job may well be live.
            if probeFailures >= MAX_PROBE_FAILURES
              console.error "[dup-guard] batchStatus probe FAILED #{probeFailures}x (hash=#{listenerHash}) — cannot determine job state; settling with an error instead of waiting forever:", e
              cleanupSocket()
              err = new Error("Could not determine whether #{eventName} is still running: the status probe failed #{probeFailures} times (#{e?.message or e}). The job may still be running on the server — reload and check before retrying.")
              err.dispatched = true
              err.probeUnavailable = true
              return reject(err)
            console.warn "[dup-guard] batchStatus probe FAILED #{probeFailures}/#{MAX_PROBE_FAILURES} (hash=#{listenerHash}); assuming STILL RUNNING to avoid a duplicate run:", e
            status = {running: true}
          if status?.done and not status?.running
            # THE LAST RESORT, not the first answer. We asked for the retained
            # completion on the way in and the server did not have it (it expired,
            # or it was never retained), so all that is left to say is that the
            # job finished and this envelope carries nothing.
            #
            # `missedCompletion` STAYS on the envelope: admin/src/Settings.coffee
            # `_readUntilPayload` re-issues a read on it (card
            # 6aa5ae2122069766ded4a323), which is the right move for a read and
            # the wrong one for an AI run — so it is the CALLER's to make, and it
            # needs this flag to make it.
            console.error "[dup-guard] (hash=#{listenerHash}) the job FINISHED server-side while the socket was away and no completion was retained for it — resolving with no result; the caller must re-read rather than re-run"
            cleanupSocket()
            resolve({data: {listener: listenerHash, done: true, missedCompletion: true}})
          else
            console.log "[dup-guard] SKIP re-fire (hash=#{listenerHash}) — job STILL RUNNING; REJOINING room and resetting #{SOCKET_IDLE_TIMEOUT_MS/1000}s timer"
            @socket?.emit 'join', listenerHash
            timeoutId = setTimeout(onTimeout, SOCKET_IDLE_TIMEOUT_MS)

        # Listen for progress updates and reset timeout
        progressHandler = (data) =>
          # Reset timeout when progress (or any activity) is received
          clearTimeout(timeoutId)
          timeoutId = setTimeout(onTimeout, SOCKET_IDLE_TIMEOUT_MS)  # checks batchStatus before giving up; never re-fires a dispatched job

          console.log "📊 Progress update received, timeout reset:", data

          # Lookup callbacks from Map (allows deregistration)
          activeCallbacks = @activeCallbacks.get(listenerHash)
          console.log "🔍 Callbacks object:", activeCallbacks
          console.log "🔍 Has onProgress?", activeCallbacks?.onProgress?
          console.log "🔍 onProgress type:", typeof activeCallbacks?.onProgress

          # Call the onProgress callback if still registered
          if activeCallbacks?.onProgress?
            console.log "✅ Calling onProgress callback with data:", data
            # Include listener hash so caller can use it for stop functionality
            dataWithListener = Object.assign({}, data, {listener: listenerHash})
            console.log "✅ Passing listener in progress:", listenerHash
            activeCallbacks.onProgress(dataWithListener)
          else
            console.log "⚠️ No onProgress callback available (may have been deregistered)"

        # Set up response listener using ClientEvent pattern (like _handleAsyncJob)
        responseHandler = (data) =>
          # Check if this is just a listener response (job queued) vs actual completion
          if data?.data?.listener?
            # Server returned a listener hash - check if it's the same as our socketAPI hash
            jobListenerHash = data.data.listener
            if jobListenerHash == listenerHash
              # ACK: the job is queued and owned server-side. From here on, never
              # re-invoke on timeout/disconnect — only re-attach.
              dispatched = true
              console.log "Socket API received same listener hash back: #{jobListenerHash}, continuing to listen..."
              # Server is using OUR hash for the job - just keep listening!
              # Don't resolve yet - wait for actual completion
              return
            else
              console.log "Socket API received DIFFERENT listener hash: #{jobListenerHash} (our hash: #{listenerHash})"
              # Different hash - this shouldn't happen in current implementation
              # But if it does, switch to the new hash
              cleanupSocket()
              clearTimeout(timeoutId)
              @_handleAsyncJob(data, callbacks).then(resolve).catch(reject)
          else
            # This is the actual completion - clean up and resolve
            cleanupSocket()
            clearTimeout(timeoutId)

            if data?.error?
              err = new Error("Socket API error: #{data.error.message || data.error}")
              err.dispatched = dispatched  # don't re-fire a job that already ran server-side
              # Carry the machine-readable discriminator across the socket
              # transport. Discarding it here made the whole socket half of the
              # error-code change inert: recovery flows key off code, not text.
              err.code = data.error.code if data.error.code?
              err.status = data.error.status if data.error.status?
              # Allowlisted structured payload — same carry as the completion
              # handler above (see the comment there).
              err.publicBody = data.error.publicBody if data.error.publicBody?
              reject(err)
            else
              resolve(data)

        # Listen for channel-specific event instead of generic transactionComplete
        @socket.on channelEvent, responseHandler
        @socket.on "transactionProgress_#{listenerHash}", progressHandler
        @socket.on 'connect', reconnectHandler

        # ENQUEUE ACK: the server emits this the moment a queued (worker) function is
        # added to BullMQ — distinct from transactionComplete (terminal) and from
        # transactionProgress (per-tick). It only marks the run as dispatched/owned so
        # the idle timer re-attaches via batchStatus instead of re-firing over HTTP.
        # This closes the duplicate-fan-out hole for jobs that sit in the queue behind a
        # backlog longer than SOCKET_IDLE_TIMEOUT_MS.
        queuedHandler = =>
          dispatched = true
          clearTimeout(timeoutId)
          timeoutId = setTimeout(onTimeout, SOCKET_IDLE_TIMEOUT_MS)
          console.log "[dup-guard] enqueue ACK (hash=#{listenerHash}) — dispatched; idle timer will re-attach, never re-fire"
          # Surface the enqueue to the caller's onProgress immediately. Launch
          # modals need the listener hash BEFORE any worker progress arrives —
          # it's the handle they poll queue state with — and a job can sit in
          # the wait list for minutes without emitting anything else.
          cbs = @activeCallbacks.get(listenerHash)
          if cbs?.onProgress?
            try cbs.onProgress({listener: listenerHash, queued: true, status: 'Queued — waiting for a worker'})
            catch e then console.warn "onProgress(queued) callback failed:", e?.message
        @socket.on "transactionQueued_#{listenerHash}", queuedHandler

        # Debug: Listen for ALL events to see what's actually coming through
        anyHandler = (eventName, ...args) =>
          if eventName.includes(listenerHash)
            console.log "🔍 Socket received event containing our hash: #{eventName}", args
        @socket.onAny anyHandler

        # Also listen for console.log events if onLog callback provided
        if callbacks?.onLog?
          @socket.on 'console.log', (data) =>
            if data?.payload?
              if Array.isArray(data.payload)
                callbacks.onLog(...data.payload)
              else
                callbacks.onLog(data.payload)
            else
              callbacks.onLog(data)

        # Set up timeout to clean up if no response received. onTimeout re-attaches
        # (never re-fires) once the job is dispatched; only rejects pre-dispatch.
        timeoutId = setTimeout(onTimeout, SOCKET_IDLE_TIMEOUT_MS)  # 2-min inactivity timer (see SOCKET_IDLE_TIMEOUT_MS)

        # Use existing socket function call pattern
        eventName = "function.#{modelName}.#{functionName}"

        # Send separated parameters to bootstrap for proper handling
        socketData =
          pathParams: pathParams
          postParams: postParams
          _responseChannel: listenerHash


        @socket.emit(eventName, socketData)

      catch e
        console.log "🚨 ERROR in callViaSocket:", e
        reject(e)

# ⚠️  WARNING: DO NOT USE ModelWrapper - UNTESTED AND SHOULD BE AVOIDED
# ModelWrapper has never been tested and may not work correctly. 
# Use direct API calls instead: model_api.findById(), model_api.updateById(), etc.
# This class should be avoided until proper testing is completed.
export class ModelWrapper  
  constructor: (@model, @obj) ->
    console.log "Constructor Model Wrapper"
    console.dir @model
    @discard()
    # Get properties from model schema if available, otherwise from object keys
    properties = @model.properties || Object.keys(@obj || {})
    for property, spec of properties
      do (property) =>  # Create closure to capture property name
        Object.defineProperty @, property,
          enumerable: true
          writable: true  # Fixed typo: was "writeable"
          get: () =>
            if property of @update
              return @update[property]
            else
              return @obj[property]
          set: (value) =>
            if value != @obj[property]
              @dirty = true
              @update[property] = value
            else if property of @update
              delete @update[property]
  
  discard: () ->
    @update = {}
    @dirty = false
    
  save: () ->
    # Use updateById instead of patchOne (which doesn't exist)
    await @model.updateById {id: @obj.id}, @update
    for key, value of @update
      @obj[key] = value
    @discard()
  
